<template>
    <div class="relative w-full">
        <!-- MOBILE -->
        <div v-if="!isDesktop">
            <div
                ref="mobileScroll"
                class="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-3"
                :class="{ 'justify-center': items.length === 1 }"
                style="
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                    scroll-padding-inline: 4%;
                "
                @scroll.passive="onMobileScroll"
            >
                <component
                    :is="item.href ? 'a' : 'div'"
                    v-for="item in items"
                    :key="`m-${item.key}`"
                    v-bind="linkAttrs(item)"
                    :aria-label="item.title"
                    class="relative h-40 w-52 flex-none snap-center overflow-hidden rounded-2xl bg-stack-text shadow-lg no-underline"
                    @click="onClick"
                >
                    <img
                        v-if="item.imageUrl"
                        :src="item.imageUrl"
                        :alt="item.imageAlt"
                        class="h-full w-full object-cover"
                    />
                    <div v-else class="h-full w-full">
                        <slot name="placeholder" :item="item"></slot>
                    </div>
                </component>
            </div>
            <div v-if="items.length > 1" class="mt-2 flex justify-center gap-2">
                <button
                    v-for="(item, i) in items"
                    :key="`dot-${item.key}`"
                    type="button"
                    :aria-label="`Ir a ${item.title}`"
                    class="h-1.5 rounded-full transition-all duration-300"
                    :class="
                        i === mobileActiveIndex
                            ? 'w-5 bg-stack-emphasis'
                            : 'w-1.5 bg-stack-text'
                    "
                    @click="scrollToMobile(i)"
                ></button>
            </div>
        </div>

        <template v-else>
            <!-- DESKTOP -->
            <div
                ref="desktopContainer"
                class="relative select-none overflow-hidden"
                style="height: 240px"
            >
                <div
                    class="absolute inset-y-0 flex items-center transition-transform duration-500 ease-out"
                    :style="trackStyle"
                >
                    <component
                        :is="isActiveLink(item, index) ? 'a' : 'div'"
                        v-for="(item, index) in items"
                        :key="`d-${item.key}`"
                        v-bind="
                            isActiveLink(item, index) ? linkAttrs(item) : {}
                        "
                        :aria-label="item.title"
                        :tabindex="index === activeIndex ? 0 : -1"
                        class="relative flex-none overflow-hidden rounded-2xl bg-stack-text shadow-2xl no-underline transition-all duration-500 ease-out"
                        :style="getItemStyle(index)"
                        @click="onDesktopItemClick($event, index)"
                    >
                        <div
                            class="absolute inset-0 h-full w-full transform transition-transform duration-700"
                            :class="
                                index === activeIndex
                                    ? 'scale-100'
                                    : 'scale-110'
                            "
                        >
                            <img
                                v-if="item.imageUrl"
                                :src="item.imageUrl"
                                :alt="item.imageAlt"
                                class="h-full w-full object-cover"
                            />
                            <div v-else class="h-full w-full">
                                <slot name="placeholder" :item="item"></slot>
                            </div>
                        </div>
                    </component>
                </div>
                <button
                    v-if="items.length > 1"
                    type="button"
                    class="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 transform items-center justify-center rounded-full bg-carousel-control shadow-lg transition-all duration-200 hover:bg-white"
                    :class="
                        activeIndex === 0
                            ? 'cursor-not-allowed opacity-30'
                            : 'opacity-80 hover:opacity-100'
                    "
                    :disabled="activeIndex === 0"
                    aria-label="Anterior"
                    @click="prevItem"
                >
                    <svg
                        class="h-5 w-5 text-gray-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                </button>
                <button
                    v-if="items.length > 1"
                    type="button"
                    class="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 transform items-center justify-center rounded-full bg-carousel-control shadow-lg transition-all duration-200 hover:bg-white"
                    :class="
                        activeIndex === items.length - 1
                            ? 'cursor-not-allowed opacity-30'
                            : 'opacity-80 hover:opacity-100'
                    "
                    :disabled="activeIndex === items.length - 1"
                    aria-label="Siguiente"
                    @click="nextItem"
                >
                    <svg
                        class="h-5 w-5 text-gray-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>
            </div>

            <!-- Thumbnail strip (desktop) -->
            <div v-if="items.length > 1" class="mt-3 flex justify-center gap-2">
                <button
                    v-for="(item, i) in items"
                    :key="`t-${item.key}`"
                    type="button"
                    :aria-label="`Ver ${item.title}`"
                    class="relative h-10 w-16 flex-none transform overflow-hidden rounded-lg bg-stack-text transition-all duration-300"
                    :class="
                        i === activeIndex
                            ? 'scale-110 opacity-100 ring-2 ring-stack-emphasis'
                            : 'scale-100 opacity-40 hover:opacity-75'
                    "
                    @click="goTo(i)"
                >
                    <img
                        v-if="item.imageUrl"
                        :src="item.imageUrl"
                        :alt="item.imageAlt"
                        class="h-full w-full object-cover"
                    />
                </button>
            </div>
        </template>
    </div>
</template>

<script>
// Tamaños fijos desde el alto (activa nivel md, laterales nivel sm, 4:3),
// mismos valores que SliderV11.vue de catalog — ver
// openspec/changes/paridad-editor-bloques-markdown-banners/design.md.
const SIZES = {
    activeWidth: 299,
    activeHeight: 224,
    sideWidth: 192,
    sideHeight: 144,
    farWidth: 139,
    farHeight: 104,
    tinyWidth: 96,
    tinyHeight: 72,
    gap: 10
};

export default {
    name: "SliderV11",
    props: {
        items: { type: Array, required: true },
        previewOnly: { type: Boolean, default: false },
        isDesktop: { type: Boolean, default: true }
    },
    data: () => ({
        activeIndex: 0,
        mobileActiveIndex: 0,
        containerWidth: 0
    }),
    computed: {
        trackStyle() {
            if (!this.containerWidth || !this.items.length) return {};
            const { activeWidth, farWidth, gap, sideWidth, tinyWidth } = SIZES;
            let leftOffset = 0;
            for (let i = 0; i < this.activeIndex; i++) {
                const dist = this.activeIndex - i;
                const w =
                    dist === 1 ? sideWidth : dist === 2 ? farWidth : tinyWidth;
                leftOffset += w + gap;
            }
            const x = this.containerWidth / 2 - leftOffset - activeWidth / 2;
            return { transform: `translateX(${x}px)` };
        }
    },
    watch: {
        items() {
            this.activeIndex = 0;
            this.mobileActiveIndex = 0;
        },
        isDesktop() {
            this.$nextTick(this.observeDesktopContainer);
        }
    },
    mounted() {
        this.observeDesktopContainer();
    },
    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
    },
    methods: {
        observeDesktopContainer() {
            if (this.resizeObserver) this.resizeObserver.disconnect();
            this.updateContainerWidth();
            const el = this.$refs.desktopContainer;
            if (!el || typeof ResizeObserver === "undefined") return;
            this.resizeObserver = new ResizeObserver(this.updateContainerWidth);
            this.resizeObserver.observe(el);
        },
        updateContainerWidth() {
            const el = this.$refs.desktopContainer;
            if (el) this.containerWidth = el.getBoundingClientRect().width;
        },
        getItemStyle(index) {
            const dist = Math.abs(index - this.activeIndex);
            const base = { marginRight: `${SIZES.gap}px` };
            if (dist === 0) {
                return {
                    ...base,
                    width: `${SIZES.activeWidth}px`,
                    height: `${SIZES.activeHeight}px`,
                    opacity: 1,
                    zIndex: 10
                };
            }
            if (dist === 1) {
                return {
                    ...base,
                    width: `${SIZES.sideWidth}px`,
                    height: `${SIZES.sideHeight}px`,
                    opacity: 0.65,
                    zIndex: 5
                };
            }
            if (dist === 2) {
                return {
                    ...base,
                    width: `${SIZES.farWidth}px`,
                    height: `${SIZES.farHeight}px`,
                    opacity: 0.3,
                    zIndex: 2
                };
            }
            return {
                ...base,
                width: `${SIZES.tinyWidth}px`,
                height: `${SIZES.tinyHeight}px`,
                opacity: 0.08,
                zIndex: 1
            };
        },
        // En escritorio solo la tarjeta centrada navega: tocar una lateral
        // la trae al centro.
        isActiveLink(item, index) {
            return !!item.href && index === this.activeIndex;
        },
        onDesktopItemClick(event, index) {
            if (index !== this.activeIndex) {
                event.preventDefault();
                this.goTo(index);
                return;
            }
            this.onClick(event);
        },
        goTo(index) {
            this.activeIndex = Math.max(
                0,
                Math.min(this.items.length - 1, index)
            );
        },
        prevItem() {
            if (this.activeIndex > 0) this.activeIndex--;
        },
        nextItem() {
            if (this.activeIndex < this.items.length - 1) this.activeIndex++;
        },
        onMobileScroll() {
            const el = this.$refs.mobileScroll;
            if (!el || !el.firstElementChild) return;
            const cardWidth = el.firstElementChild.offsetWidth + 12;
            this.mobileActiveIndex = Math.round(el.scrollLeft / cardWidth);
        },
        scrollToMobile(i) {
            const el = this.$refs.mobileScroll;
            if (!el || !el.firstElementChild) return;
            const cardWidth = el.firstElementChild.offsetWidth + 12;
            el.scrollTo({ left: i * cardWidth, behavior: "smooth" });
        },
        linkAttrs(item) {
            return item.href
                ? { href: item.href, target: item.target, rel: item.rel }
                : {};
        },
        onClick(event) {
            if (this.previewOnly) event.preventDefault();
        }
    }
};
</script>
